import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BarChartIcon, CreditCardIcon, LogOutIcon, MenuIcon, XIcon, LayoutGridIcon, TerminalIcon, FileTextIcon, CalendarIcon, UsersIcon, ArrowLeftRightIcon, ShoppingBagIcon, ShoppingCartIcon, SettingsIcon, GlobeIcon, GraduationCapIcon, ChevronDownIcon, SmartphoneIcon, BanknoteIcon, MegaphoneIcon, HomeIcon, BedDoubleIcon, ClipboardListIcon, DollarSignIcon, BookOpenIcon, TruckIcon, FileIcon, AlertTriangleIcon, RefreshCwIcon } from 'lucide-react';
interface AppLayoutProps {
  children: React.ReactNode;
}
export function AppLayout({
  children
}: AppLayoutProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [terminalsExpanded, setTerminalsExpanded] = useState(false);
  const [transactionsExpanded, setTransactionsExpanded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = (path: string) => {
    if (path === '/overview') {
      return location.pathname === '/overview' || location.pathname === '/';
    }
    return location.pathname === path;
  };
  const isPaymentRequestsActive = () => {
    return location.pathname === '/payment-requests';
  };
  // Check if any transactions path is active
  const isTransactionsActive = () => {
    return ['/sales', '/settlements', '/disputes', '/refunds'].some(path => location.pathname.startsWith(path));
  };
  // Get the current page title based on the URL
  const getPageTitle = () => {
    if (location.pathname === '/overview' || location.pathname === '/') return 'Overview';
    if (location.pathname === '/payment-requests') return 'Payment Requests';
    if (location.pathname === '/settings') return 'Settings';
    if (location.pathname === '/virtual-terminal') return 'Virtual Terminal';
    if (location.pathname === '/physical-terminals') return 'Physical Terminals';
    if (location.pathname === '/softpos-terminals') return 'SoftPos Terminals';
    if (location.pathname === '/connect') return 'Connect OAuth2 Apps';
    if (location.pathname === '/orders') return 'Orders';
    if (location.pathname === '/tutorials') return 'Tutorials';
    if (location.pathname === '/campaigns') return 'Campaigns';
    if (location.pathname === '/campaign-details') return 'Campaign Details';
    if (location.pathname === '/campaign-form') return location.search.includes('id=') ? 'Update Campaign' : 'Create Campaign';
    if (location.pathname === '/invoices') return 'Invoices';
    if (location.pathname.startsWith('/invoice-detail/')) return 'Invoice Details';
    if (location.pathname === '/invoice-form') return location.search.includes('id=') ? 'Edit Invoice' : 'Create Invoice';
    if (location.pathname === '/customers') return 'Customers';
    if (location.pathname.startsWith('/customer-detail/')) return 'Customer Details';
    if (location.pathname === '/customer-form') return location.search.includes('id=') ? 'Edit Customer' : 'Create Customer';
    if (location.pathname === '/sales') return 'Sales';
    if (location.pathname.startsWith('/sale-detail/')) return 'Sale Details';
    if (location.pathname === '/settlements') return 'Settlements';
    if (location.pathname.startsWith('/settlement-detail/')) return 'Settlement Details';
    if (location.pathname === '/disputes') return 'Disputes';
    if (location.pathname.startsWith('/dispute-detail/')) return 'Dispute Details';
    if (location.pathname === '/refunds') return 'Refunds';
    if (location.pathname.startsWith('/refund-detail/')) return 'Refund Details';
    if (location.pathname === '/suppliers') return 'Suppliers';
    if (location.pathname.startsWith('/supplier-detail/')) return 'Supplier Details';
    if (location.pathname === '/supplier-form') return location.search.includes('id=') ? 'Edit Supplier' : 'Create Supplier';
    if (location.pathname === '/bills') return 'Bills';
    if (location.pathname.startsWith('/bill-detail/')) return 'Bill Details';
    if (location.pathname === '/bill-form') return location.search.includes('id=') ? 'Edit Bill' : 'Create Bill';
    if (location.pathname.startsWith('/bill-approval/')) return 'Bill Approval';
    if (location.pathname.startsWith('/bill-financing/')) return 'Request Bill Financing';
    if (location.pathname.startsWith('/tutorial/')) return 'Tutorial';
    return 'Dashboard';
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-150">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-0 right-0 p-4 z-20" data-id="element-151">
        <button onClick={() => setMenuOpen(!menuOpen)} className="p-2 rounded-md bg-white shadow-sm border border-gray-200" data-id="element-152">
          {menuOpen ? <XIcon size={20} data-id="element-153" /> : <MenuIcon size={20} data-id="element-154" />}
        </button>
      </div>
      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 w-64 bg-gray-100 border-r border-gray-300 z-10 transform transition-transform duration-200 ease-in-out
        ${menuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `} data-id="element-155">
        <div className="h-full flex flex-col" data-id="element-156">
          {/* Logo */}
          <div className="p-4 border-b border-gray-300 flex items-center justify-center" data-id="element-157">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/q86izFaWmRFfWc2CK9tiZz/logo.png" alt="DFCC Bank" className="h-12" data-id="element-158" />
          </div>
          {/* Navigation */}
          <nav className="flex-1 py-4 space-y-1 text-gray-700" data-id="element-159">
            <button onClick={() => navigate('/overview')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/overview') ? 'bg-blue-600 text-white' : 'hover:bg-gray-200'}`} data-id="element-160">
              <BarChartIcon size={18} className="mr-3" data-id="element-161" />
              Overview
            </button>
            <button onClick={() => navigate('/payment-requests')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/payment-requests') ? 'bg-blue-600 text-white' : 'hover:bg-gray-200'}`} data-id="element-162">
              <LayoutGridIcon size={18} className="mr-3" data-id="element-163" />
              Payment requests
            </button>
            {/* Terminals Collapsible Menu */}
            <div data-id="element-164">
              <button onClick={() => setTerminalsExpanded(!terminalsExpanded)} className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium ${isActive('/virtual-terminal') || isActive('/physical-terminals') || isActive('/softpos-terminals') || isActive('/connect') ? 'bg-blue-600 text-white' : 'hover:bg-gray-200'}`} data-id="element-165">
                <div className="flex items-center" data-id="element-166">
                  <TerminalIcon size={18} className="mr-3" data-id="element-167" />
                  Terminals
                </div>
                <ChevronDownIcon size={16} className={`transition-transform ${terminalsExpanded ? 'rotate-180' : ''}`} data-id="element-168" />
              </button>
              {/* Terminal submenu items */}
              {terminalsExpanded && <div className="ml-7 pl-4 border-l border-gray-300" data-id="element-169">
                  <button onClick={() => navigate('/virtual-terminal')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/virtual-terminal') ? 'text-blue-600 font-semibold' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-170">
                    <BanknoteIcon size={16} className="mr-3" data-id="element-171" />
                    Virtual
                  </button>
                  <button onClick={() => navigate('/physical-terminals')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/physical-terminals') ? 'text-blue-600 font-semibold' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-172">
                    <CreditCardIcon size={16} className="mr-3" data-id="element-173" />
                    Physical
                  </button>
                  <button onClick={() => navigate('/softpos-terminals')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/softpos-terminals') ? 'text-blue-600 font-semibold' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-174">
                    <SmartphoneIcon size={16} className="mr-3" data-id="element-175" />
                    SoftPos
                  </button>
                  <button onClick={() => navigate('/connect')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/connect') ? 'text-blue-600 font-semibold' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-176">
                    <GlobeIcon size={16} className="mr-3" data-id="element-177" />
                    Connect
                  </button>
                </div>}
            </div>
            <button onClick={() => navigate('/invoices')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/invoices') ? 'bg-blue-600 text-white' : 'hover:bg-gray-200'}`} data-id="element-178">
              <FileTextIcon size={18} className="mr-3" data-id="element-179" />
              Invoices
            </button>
            <button onClick={() => navigate('/bills')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/bills') || location.pathname.startsWith('/bill-detail/') || isActive('/bill-form') || location.pathname.startsWith('/bill-approval/') || location.pathname.startsWith('/bill-financing/') ? 'bg-blue-600 text-white' : 'hover:bg-gray-200'}`} data-id="element-180">
              <FileIcon size={18} className="mr-3" data-id="element-181" />
              Bills
            </button>
            <div className="border-t border-gray-300" data-id="element-182"></div>
            <button onClick={() => navigate('/customers')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/customers') ? 'bg-blue-600 text-white' : 'hover:bg-gray-200'}`} data-id="element-183">
              <UsersIcon size={18} className="mr-3" data-id="element-184" />
              Customers
            </button>
            <button onClick={() => navigate('/suppliers')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/suppliers') || location.pathname.startsWith('/supplier-detail/') || isActive('/supplier-form') ? 'bg-blue-600 text-white' : 'hover:bg-gray-200'}`} data-id="element-185">
              <TruckIcon size={18} className="mr-3" data-id="element-186" />
              Suppliers
            </button>
            <button onClick={() => setTransactionsExpanded(!transactionsExpanded)} className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium ${isTransactionsActive() ? 'bg-blue-600 text-white' : 'hover:bg-gray-200'}`} data-id="element-187">
              <div className="flex items-center" data-id="element-188">
                <ArrowLeftRightIcon size={18} className="mr-3" data-id="element-189" />
                Transactions
              </div>
              <ChevronDownIcon size={16} className={`transition-transform ${transactionsExpanded ? 'rotate-180' : ''}`} data-id="element-190" />
            </button>
            {/* Transactions submenu items */}
            {transactionsExpanded && <div className="ml-7 pl-4 border-l border-gray-300" data-id="element-191">
                <button onClick={() => navigate('/sales')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/sales') || location.pathname.startsWith('/sale-detail/') ? 'text-blue-600 font-semibold' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-192">
                  <CreditCardIcon size={16} className="mr-3" data-id="element-193" />
                  Sales
                </button>
                <button onClick={() => navigate('/settlements')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/settlements') || location.pathname.startsWith('/settlement-detail/') ? 'text-blue-600 font-semibold' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-194">
                  <BanknoteIcon size={16} className="mr-3" data-id="element-195" />
                  Settlements
                </button>
                <button onClick={() => navigate('/disputes')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/disputes') || location.pathname.startsWith('/dispute-detail/') ? 'text-blue-600 font-semibold' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-196">
                  <AlertTriangleIcon size={16} className="mr-3" data-id="element-197" />
                  Disputes
                </button>
                <button onClick={() => navigate('/refunds')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/refunds') || location.pathname.startsWith('/refund-detail/') ? 'text-blue-600 font-semibold' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-198">
                  <RefreshCwIcon size={16} className="mr-3" data-id="element-199" />
                  Refunds
                </button>
              </div>}
            <button onClick={() => navigate('/shops')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/shops') ? 'bg-blue-600 text-white' : 'hover:bg-gray-200'}`} data-id="element-200">
              <ShoppingBagIcon size={18} className="mr-3" data-id="element-201" />
              Shops
            </button>
            <button onClick={() => navigate('/orders')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/orders') ? 'bg-blue-600 text-white' : 'hover:bg-gray-200'}`} data-id="element-202">
              <ShoppingCartIcon size={18} className="mr-3" data-id="element-203" />
              Orders
            </button>
            <button onClick={() => navigate('/campaigns')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/campaigns') || isActive('/campaign-details') || isActive('/campaign-form') ? 'bg-blue-600 text-white' : 'hover:bg-gray-200'}`} data-id="element-204">
              <MegaphoneIcon size={18} className="mr-3" data-id="element-205" />
              Campaigns
            </button>
            <div className="border-t border-gray-300" data-id="element-206"></div>
            <button onClick={() => navigate('/settings')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/settings') ? 'bg-blue-600 text-white' : 'hover:bg-gray-200'}`} data-id="element-207">
              <SettingsIcon size={18} className="mr-3" data-id="element-208" />
              Settings
            </button>
            <button onClick={() => navigate('/tutorials')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/tutorials') ? 'bg-blue-600 text-white' : 'hover:bg-gray-200'}`} data-id="element-209">
              <GraduationCapIcon size={18} className="mr-3" data-id="element-210" />
              Tutorials
            </button>
          </nav>
        </div>
      </div>
      {/* Main content */}
      <div className="flex-1 lg:pl-64" data-id="element-211">
        <div className="bg-white border-b border-gray-200" data-id="element-212">
          <div className="flex justify-between items-center px-6 py-4" data-id="element-213">
            <h1 className="text-xl font-semibold text-gray-900" data-id="element-214">
              {getPageTitle()}
            </h1>
            <div className="flex items-center" data-id="element-215">
              <div className="w-10 h-10 bg-[#f8d7d7] rounded-full flex items-center justify-center text-[#e54c3c] font-medium" data-id="element-216">
                SV
              </div>
              <div className="ml-3 hidden sm:block" data-id="element-217">
                <p className="text-sm font-medium text-gray-800" data-id="element-218">
                  Simon Verraest
                </p>
                <p className="text-xs text-gray-500" data-id="element-219">CEYLON TEA CO.</p>
              </div>
              <ChevronDownIcon size={16} className="ml-2 text-gray-500" data-id="element-220" />
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-221">
          {children}
        </div>
      </div>
      {/* Overlay for mobile */}
      {menuOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-0 lg:hidden" onClick={() => setMenuOpen(false)} data-id="element-222"></div>}
    </div>;
}